/**
 * TrackProgress — where the track is, as the page's navigation, and the one
 * thing to do now.
 *
 * The steps (participants → pool → criteria → scales → values → sessions →
 * published result) are read from the server's readiness check and the result
 * published for the track (trackStory). Each step is a button that opens that
 * step below; the step the next thing points at is marked. Under them sits the
 * page's only call to action. The "why" sentence under it is drawn only with
 * the `story_overlay` module on (sales and education workspaces).
 */
import { Link } from 'react-router-dom';
import { StateMark } from '@/components/ds';
import { LABEL_MONO, btnPrimary, btnSecondary, heading, panel } from '@/components/mono/primitives';
import type { StoryStopId, TrackStory } from '@/lib/tracks/trackStory';
import type { TrackView } from '@/lib/tracks/workspaceTab';
import { useTossDrawerStore } from '@/stores/prioritization/tossDrawerStore';

export interface TrackProgressProps {
  story: TrackStory;
  cycleName: string | null;
  /** What is open below the strip; null while the facts are still being read. */
  view: TrackView | null;
  onOpen: (step: StoryStopId) => void;
  /** Draw why the next thing matters (module `story_overlay`). */
  explain: boolean;
}

export function TrackProgress({ story, cycleName, view, onOpen, explain }: TrackProgressProps) {
  const { stops, next } = story;
  const current = next?.stop ?? null;
  const act = next?.act ?? null;
  // The act belongs to the step already open right below, which holds the same control: leave it out.
  const showAct = act !== null && !(act.step !== null && act.step === view);

  return (
    <div className="flex flex-col gap-2.5" data-testid="track-progress">
      <nav aria-label="Where this track is" {...panel('px-4 pt-3 pb-2.5')}>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 {...heading()}>Where this track is</h2>
          <span className="text-xs" style={{ color: 'var(--text-ter)' }}>
            {cycleName ? `In ${cycleName}` : 'Standalone · in no cycle'}
          </span>
        </div>
        <ol className="mt-2 grid list-none gap-1.5 p-0" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))' }}>
          {stops.map((s) => {
            const here = s.id === current;
            const open = s.id === view;
            const tone = here ? 'var(--mustard)' : s.state === 'done' ? 'var(--green)' : 'var(--rule-bright)';
            return (
              <li key={s.id} className="min-w-0">
                <button
                  type="button"
                  onClick={() => onOpen(s.id)}
                  aria-current={open ? 'page' : undefined}
                  data-testid={`track-step-${s.id}`}
                  className="block w-full min-w-0 cursor-pointer px-1.5 pb-1.5 pt-1.5 text-left hover:bg-[var(--surface)]"
                  style={{
                    borderTop: `2px solid ${tone}`,
                    background: open ? 'var(--surface)' : 'transparent',
                    boxShadow: open ? 'inset 0 -2px 0 var(--text-pri)' : undefined,
                  }}
                >
                  <span className="flex items-center gap-1.5 text-xs font-medium" style={{ color: 'var(--text-pri)' }}>
                    <StateMark state={s.state ?? 'open'} />
                    <span className="truncate">{s.label}</span>
                    {here && <span className="sr-only"> (next)</span>}
                  </span>
                  <span className="mt-0.5 block truncate text-xs" style={{ color: 'var(--text-sec)' }} title={typeof s.value === 'string' ? s.value : undefined}>
                    {s.value}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </nav>

      <section aria-label="The one thing to do now" className="px-4 py-3" style={{ background: 'var(--mustard-dim)', borderLeft: '3px solid var(--mustard)' }}>
        <span className={LABEL_MONO} style={{ color: 'var(--mustard)' }}>
          The one thing to do now
        </span>
        {next === null ? (
          <p className="mt-1 text-sm" style={{ color: 'var(--text-sec)' }}>
            Checking…
          </p>
        ) : (
          <>
            <h3 className="mt-1 text-base font-semibold leading-snug" style={{ color: 'var(--text-pri)' }}>
              {next.title}
            </h3>
            {(explain || next.detail) && (
              <p className="mt-1 max-w-[62ch] text-sm" style={{ color: 'var(--text-sec)' }}>
                {explain ? next.why : ''}
                {next.detail ? `${explain ? ' ' : ''}Right now: ${next.detail}.` : ''}
              </p>
            )}
            {((showAct && act) || next.waitsOn) && (
              <div className="mt-2.5 flex flex-wrap items-center gap-2">
                {showAct && act && act.go.kind === 'href' && (
                  <Link to={act.go.href} {...(next.settled ? btnSecondary() : btnPrimary())}>
                    {act.label}
                  </Link>
                )}
                {showAct && act && act.go.kind === 'toss' && (
                  <button
                    type="button"
                    onClick={() => act.go.kind === 'toss' && useTossDrawerStore.getState().openToss(act.go.cycleId, act.go.trackId)}
                    {...btnPrimary()}
                  >
                    {act.label}
                  </button>
                )}
                {showAct && act && act.go.kind === 'step' && (
                  <button type="button" onClick={() => act.go.kind === 'step' && onOpen(act.go.step)} {...btnPrimary()}>
                    {act.label}
                  </button>
                )}
                {next.waitsOn && (
                  <span className="text-xs" style={{ color: 'var(--text-ter)' }}>
                    {next.waitsOn}
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

export default TrackProgress;
